import React, { useRef, useState, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import {
    ClipboardList, Search, ShieldCheck, Wrench, Sparkles, CheckCircle2,
    Activity, Loader2, AlertCircle, Calendar, ChevronRight, Phone, User,
    ChevronDown, Zap, Clock
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTenantTheme } from '../TenantThemeContext';
import {
    GlassCard, RevealOnScroll, SectionHeader, MagneticButton, AmbientBlob, GridBackdrop
} from '../ui/primitives';
import SectionParticles from '../ui/SectionParticles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== PROCESS / HOW IT WORKS ==============
const STEPS = [
    { icon: ClipboardList, en: 'Drop-off & inspection', bn: 'গাড়ি জমা ও পরিদর্শন' },
    { icon: Search, en: 'Honest diagnosis', bn: 'সঠিক রোগনির্ণয়' },
    { icon: ShieldCheck, en: 'Approval & quote', bn: 'অনুমোদন ও কোটেশন' },
    { icon: Wrench, en: 'Skilled repair', bn: 'দক্ষ মেরামত' },
    { icon: Sparkles, en: 'Quality test', bn: 'মান পরীক্ষা' },
    { icon: CheckCircle2, en: 'Pickup & warranty', bn: 'বুঝে নেওয়া ও ওয়ারেন্টি' }
];

export function Process() {
    const { sectionEnabled } = useTenantTheme();
    const { language } = useLanguage();
    const [activeIdx, setActiveIdx] = useState(null);
    const sectionRef = useRef(null);
    // Scroll-linked progress for the rail. Starts filling when the section
    // approaches the viewport and completes as it scrolls past.
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start 80%', 'end 30%']
    });
    const railScaleX = useSpring(scrollYProgress, { stiffness: 80, damping: 22, mass: 0.4 });
    if (!sectionEnabled('process')) return null;
    // Hover takes priority over scroll progress.
    const hoverPct = activeIdx === null
        ? null
        : ((activeIdx + 1) / STEPS.length);
    return (
        <section ref={sectionRef} className="relative py-16 md:py-20 bg-[var(--tenant-surface-alt)]/30" id="process">
            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <SectionHeader
                    eyebrow={language === 'bn' ? 'কীভাবে কাজ করি' : 'How we work'}
                    title="Six steps. Zero surprises."
                    titleBn="ছয় ধাপ। কোনো চমক নেই।"
                />
                <div
                    className="mt-14 relative"
                    onMouseLeave={() => setActiveIdx(null)}
                >
                    {/* Base rail */}
                    <div className="absolute top-9 left-0 right-0 h-px bg-[var(--tenant-border)] hidden md:block" />
                    {/* Highlighted progress rail — driven by scroll, overridden by hover. */}
                    {hoverPct !== null ? (
                        <div
                            className="absolute top-9 left-0 h-[2px] hidden md:block bg-[var(--tenant-primary)] transition-[width] duration-500 ease-out"
                            style={{ width: `${hoverPct * 100}%` }}
                        />
                    ) : (
                        <motion.div
                            aria-hidden
                            className="absolute top-9 left-0 right-0 h-[2px] hidden md:block bg-[var(--tenant-primary)] origin-left"
                            style={{ scaleX: railScaleX }}
                        />
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-12 gap-x-4 relative">
                        {STEPS.map((step, i) => {
                            const Icon = step.icon;
                            const isActive = activeIdx !== null && i <= activeIdx;
                            const isCurrent = activeIdx === i;
                            return (
                                <RevealOnScroll key={i} delay={i * 0.07} className="relative flex flex-col items-center text-center">
                                    <div
                                        onMouseEnter={() => setActiveIdx(i)}
                                        onFocus={() => setActiveIdx(i)}
                                        tabIndex={0}
                                        className={[
                                            'group relative w-[72px] h-[72px] rounded-2xl flex items-center justify-center cursor-pointer',
                                            'border transition-all duration-300 ease-out',
                                            'shadow-[0_4px_20px_-8px_rgba(0,0,0,0.3)]',
                                            isCurrent
                                                ? '-translate-y-1.5 scale-[1.06] bg-[var(--tenant-primary)] border-[var(--tenant-primary)] shadow-[0_18px_40px_-18px_var(--tenant-primary)]'
                                                : isActive
                                                    ? 'bg-[var(--tenant-primary)]/10 border-[var(--tenant-primary)]/40'
                                                    : 'bg-[var(--tenant-surface)] border-[var(--tenant-border)] hover:-translate-y-1 hover:border-[var(--tenant-primary)]/40'
                                        ].join(' ')}
                                    >
                                        <Icon className={[
                                            'w-7 h-7 transition-colors duration-300',
                                            isCurrent ? 'text-[var(--tenant-surface)]' : 'text-[var(--tenant-primary)]'
                                        ].join(' ')} />
                                        <span className={[
                                            'absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-display tracking-wider transition-colors duration-300',
                                            isCurrent
                                                ? 'bg-[var(--tenant-surface)] text-[var(--tenant-primary)]'
                                                : 'bg-[var(--tenant-primary)] text-[var(--tenant-surface)]'
                                        ].join(' ')}>
                                            {i + 1}
                                        </span>
                                    </div>
                                    <div className={[
                                        'mt-4 text-sm font-medium transition-colors duration-300',
                                        isCurrent ? 'text-[var(--tenant-primary)]' : 'text-[var(--tenant-ink)]',
                                        language === 'bn' ? 'font-bn text-base' : ''
                                    ].join(' ')}>
                                        {language === 'bn' ? step.bn : step.en}
                                    </div>
                                </RevealOnScroll>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ============== LIVE JOB TRACKER ==============
export function JobTracker({ slug }) {
    const { sectionEnabled } = useTenantTheme();
    const { language } = useLanguage();
    const [phone, setPhone] = useState('');
    const [jobNo, setJobNo] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    // Collapsed-by-default UX matching the Booking section.
    const [expanded, setExpanded] = useState(false);

    React.useEffect(() => {
        const onHash = () => {
            const h = window.location.hash;
            if (h === '#tracker' || h === '#track-job') setExpanded(true);
        };
        onHash();
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    if (!sectionEnabled('job_tracker')) return null;

    async function lookup(e) {
        e?.preventDefault();
        if (!phone.trim()) {
            setError(language === 'bn' ? 'ফোন নম্বর দিন' : 'Enter your phone number');
            return;
        }
        setError('');
        setLoading(true);
        setResult(null);
        try {
            const params = { phone: phone.trim() };
            if (jobNo.trim()) params.job_no = jobNo.trim();
            const res = await axios.get(`${API}/tenants/${slug}/track`, { params });
            setResult(res.data);
        } catch (e) {
            const status = e?.response?.status;
            setError(status === 404
                ? (language === 'bn' ? 'এই নম্বরে কোনো জব পাওয়া যায়নি' : 'No jobs found for this phone')
                : status === 403
                    ? (language === 'bn' ? 'ফোন নম্বর মিলছে না' : 'Phone does not match')
                    : (language === 'bn' ? 'খুঁজতে সমস্যা হয়েছে। আবার চেষ্টা করুন।' : 'Lookup failed. Try again.'));
        } finally {
            setLoading(false);
        }
    }

    function reset() {
        setResult(null);
        setError('');
    }

    function collapse() {
        setExpanded(false);
        setResult(null);
        setError('');
    }

    return (
        <section className="relative py-16 md:py-20" id="tracker">
            <AmbientBlob className="-top-20 left-1/2 -translate-x-1/2" size={480} />
            <div className="relative max-w-5xl mx-auto px-5 md:px-8">
                <SectionHeader
                    align="center"
                    eyebrow={language === 'bn' ? 'লাইভ ট্র্যাকিং' : 'Live job tracker'}
                    title="Track your service in real time"
                    titleBn="আপনার সার্ভিসের অগ্রগতি দেখুন"
                />

                {!expanded && (
                    <RevealOnScroll className="mt-10">
                        <GlassCard className="p-6 md:p-8 max-w-3xl mx-auto">
                            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] text-[11px] uppercase tracking-[0.22em] font-semibold">
                                        <Activity className="w-3.5 h-3.5" />
                                        {language === 'bn' ? 'লাইভ স্ট্যাটাস' : 'Live status'}
                                    </div>
                                    <h3 className={['mt-3 font-display text-2xl md:text-3xl tracking-wide text-[var(--tenant-ink)]', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                                        {language === 'bn' ? 'আপনার সার্ভিস কোন ধাপে?' : 'Where is your service right now?'}
                                    </h3>
                                    <p className={['mt-2 text-[var(--tenant-muted)] text-sm md:text-[15px] leading-relaxed', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                                        {language === 'bn'
                                            ? 'ফোন নম্বর দিন — রিয়েল-টাইম স্ট্যাটাস ও প্রত্যাশিত সময় দেখুন।'
                                            : 'Enter your phone — see real-time status & expected pickup time.'}
                                    </p>
                                </div>
                                <div className="flex md:justify-end">
                                    <MagneticButton
                                        as="button"
                                        type="button"
                                        onClick={() => setExpanded(true)}
                                        size="lg"
                                        icon={<Activity className="w-4 h-4" />}
                                    >
                                        {language === 'bn' ? 'ট্র্যাক শুরু করুন' : 'Track my job'}
                                    </MagneticButton>
                                </div>
                            </div>
                        </GlassCard>
                    </RevealOnScroll>
                )}

                <AnimatePresence initial={false}>
                    {expanded && (
                        <motion.div
                            key="tracker-expanded"
                            initial={{ opacity: 0, y: -8, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -8, height: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            style={{ overflow: 'hidden' }}
                        >
                            <div className="flex justify-end mt-6 mb-2 max-w-2xl mx-auto">
                                <button
                                    type="button"
                                    onClick={collapse}
                                    className="text-xs uppercase tracking-[0.22em] text-[var(--tenant-muted)] hover:text-[var(--tenant-primary)] transition-colors"
                                >
                                    {language === 'bn' ? '× বন্ধ করুন' : '× Close'}
                                </button>
                            </div>
                <form onSubmit={lookup} className="mt-2 max-w-2xl mx-auto">
                    <GlassCard className="p-3 flex flex-col md:flex-row gap-2">
                        <div className="flex-1 flex items-center gap-2 px-3">
                            <Phone className="w-4 h-4 text-[var(--tenant-muted)]" />
                            <input
                                type="tel"
                                inputMode="tel"
                                placeholder={language === 'bn' ? 'আপনার ফোন' : 'Your phone'}
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-[var(--tenant-ink)] py-3"
                            />
                        </div>
                        <div className="flex-1 flex items-center gap-2 px-3 border-t md:border-t-0 md:border-l border-[var(--tenant-border)]">
                            <ClipboardList className="w-4 h-4 text-[var(--tenant-muted)]" />
                            <input
                                type="text"
                                placeholder={language === 'bn' ? 'জব নম্বর (ঐচ্ছিক)' : 'Job number (optional)'}
                                value={jobNo}
                                onChange={e => setJobNo(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-[var(--tenant-ink)] py-3"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] font-semibold uppercase tracking-wide text-sm hover:brightness-110 transition disabled:opacity-60"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                            {language === 'bn' ? 'খুঁজুন' : 'Track'}
                        </button>
                    </GlassCard>
                    {error && (
                        <div className="mt-4 inline-flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="w-4 h-4" /> {error}
                        </div>
                    )}
                </form>

                {result && (
                    <RevealOnScroll className="mt-8 max-w-2xl mx-auto space-y-4">
                        {(result.jobs || [result]).map((job, idx) => (
                            <GlassCard key={job.job_no || idx} className="p-6">
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)]">Job #</div>
                                        <div className="font-headline font-extrabold text-3xl text-[var(--tenant-ink)] tracking-tight">{job.job_no}</div>
                                    </div>
                                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--tenant-primary)]/15 text-[var(--tenant-primary)] text-xs uppercase tracking-[0.22em] font-semibold">
                                        {job.status?.replace(/_/g, ' ')}
                                    </span>
                                </div>
                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <div className="text-[var(--tenant-muted)]">{language === 'bn' ? 'কাস্টমার' : 'Customer'}</div>
                                        <div className="font-medium text-[var(--tenant-ink)]">{job.customer_name}</div>
                                    </div>
                                    {job.bike && (
                                        <div>
                                            <div className="text-[var(--tenant-muted)]">{language === 'bn' ? 'গাড়ি' : 'Vehicle'}</div>
                                            <div className="font-medium text-[var(--tenant-ink)]">{[job.bike.make, job.bike.model].filter(Boolean).join(' ')}</div>
                                        </div>
                                    )}
                                    {job.expected_completion && (
                                        <div>
                                            <div className="text-[var(--tenant-muted)]">{language === 'bn' ? 'প্রত্যাশিত' : 'Expected'}</div>
                                            <div className="font-medium text-[var(--tenant-ink)]">{new Date(job.expected_completion).toLocaleString()}</div>
                                        </div>
                                    )}
                                    {job.created_at && (
                                        <div>
                                            <div className="text-[var(--tenant-muted)]">{language === 'bn' ? 'তৈরি' : 'Booked'}</div>
                                            <div className="font-medium text-[var(--tenant-ink)]">{new Date(job.created_at).toLocaleDateString()}</div>
                                        </div>
                                    )}
                                </div>
                                {job.service_summary && (
                                    <p className="mt-4 text-sm text-[var(--tenant-muted)]">{job.service_summary}</p>
                                )}
                            </GlassCard>
                        ))}
                        <div className="text-center pt-2">
                            <button
                                type="button"
                                onClick={reset}
                                className="text-xs uppercase tracking-[0.22em] text-[var(--tenant-primary)] hover:brightness-110 transition"
                            >
                                {language === 'bn' ? '↺ আবার ট্র্যাক করুন' : '↺ Track another job'}
                            </button>
                        </div>
                    </RevealOnScroll>
                )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
}

// ============== BOOKING FORM ==============
function todayISO() {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
}

export function Booking({ slug, services = [] }) {
    const { sectionEnabled, tenant } = useTenantTheme();
    const { language } = useLanguage();
    const [step, setStep] = useState(1);
    const [date, setDate] = useState(todayISO());
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [time, setTime] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [form, setForm] = useState({ customer_name: '', phone: '', bike_make: '', bike_model: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [error, setError] = useState('');
    // Collapsed-by-default UX: form opens only when the user asks for it.
    const [expanded, setExpanded] = useState(false);

    const minDate = useMemo(() => todayISO(), []);

    React.useEffect(() => {
        if (!sectionEnabled('booking')) return;
        let cancelled = false;
        setSlotsLoading(true);
        axios.get(`${API}/tenants/${slug}/booking-slots`, { params: { date } })
            .then(res => { if (!cancelled) setSlots(res.data?.slots || []); })
            .catch(() => { if (!cancelled) setSlots([]); })
            .finally(() => { if (!cancelled) setSlotsLoading(false); });
        return () => { cancelled = true; };
    }, [date, slug, sectionEnabled]);

    // Auto-expand the form when navigated via #booking / #book-now anchor —
    // any "Book a slot" CTA elsewhere on the page opens the form for the user.
    React.useEffect(() => {
        const onHash = () => {
            const h = window.location.hash;
            if (h === '#booking' || h === '#book-now') setExpanded(true);
        };
        onHash();
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    if (!sectionEnabled('booking')) return null;

    function update(k, v) { setForm(f => ({ ...f, [k]: v })); }

    async function submit(e) {
        e?.preventDefault();
        if (!form.customer_name || !form.phone || !time) {
            setError(language === 'bn' ? 'নাম, ফোন ও সময় প্রয়োজন' : 'Name, phone and time are required');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const res = await axios.post(`${API}/tenants/${slug}/bookings`, {
                ...form,
                service_type: serviceType,
                preferred_date: date,
                preferred_time: time
            });
            setDone(res.data);
        } catch (err) {
            setError(err?.response?.data?.detail || (language === 'bn' ? 'বুকিং পাঠানো যায়নি' : 'Could not submit'));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section className="relative py-16 md:py-20 bg-[var(--tenant-surface-alt)]/30" id="booking">
            <SectionParticles density={28} repel={0.07} />
            <div className="relative max-w-5xl mx-auto px-5 md:px-8">
                <SectionHeader
                    align="center"
                    eyebrow={language === 'bn' ? 'বুকিং' : 'Book a slot'}
                    title="Reserve your time"
                    titleBn="আপনার সময় বুক করুন"
                />
                <RevealOnScroll className="mt-10">
                    {/* Compact CTA card — collapsed state. Saves vertical space
                        until the user asks to book. */}
                    {!expanded && !done && (
                        <GlassCard className="p-6 md:p-8 overflow-hidden relative">
                            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] text-[11px] uppercase tracking-[0.22em] font-semibold">
                                        <Zap className="w-3.5 h-3.5" />
                                        {language === 'bn' ? 'মাত্র ৩০ সেকেন্ড' : 'Takes ~30 seconds'}
                                    </div>
                                    <h3 className={['mt-3 font-display text-3xl md:text-4xl tracking-wide text-[var(--tenant-ink)]', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                                        {language === 'bn' ? 'স্লট রিজার্ভ করুন' : 'Reserve a workshop slot'}
                                    </h3>
                                    <p className={['mt-2 text-[var(--tenant-muted)] text-[15px] leading-relaxed max-w-xl', language === 'bn' ? 'font-bn' : ''].join(' ')}>
                                        {language === 'bn'
                                            ? 'তারিখ ও সময় বেছে নিন — আমরা কল করে নিশ্চিত করব। কোনো অগ্রিম পেমেন্ট নয়।'
                                            : 'Pick a date and time — we ring back to confirm. No upfront payment.'}
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs uppercase tracking-[0.18em] text-[var(--tenant-muted)] font-medium">
                                        <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[var(--tenant-primary)]" />{language === 'bn' ? 'তারিখ' : 'Pick date'}</span>
                                        <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[var(--tenant-primary)]" />{language === 'bn' ? 'খালি সময়' : 'Choose slot'}</span>
                                        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[var(--tenant-primary)]" />{language === 'bn' ? 'নিশ্চিত' : 'Confirmed'}</span>
                                    </div>
                                </div>
                                <div className="flex md:justify-end">
                                    <MagneticButton
                                        as="button"
                                        type="button"
                                        onClick={() => setExpanded(true)}
                                        size="lg"
                                        icon={<ChevronDown className="w-4 h-4" />}
                                    >
                                        {language === 'bn' ? 'বুকিং শুরু করুন' : 'Start booking'}
                                    </MagneticButton>
                                </div>
                            </div>
                        </GlassCard>
                    )}

                    <AnimatePresence initial={false}>
                        {(expanded || done) && (
                            <motion.div
                                key="booking-form"
                                initial={{ opacity: 0, y: -8, height: 0 }}
                                animate={{ opacity: 1, y: 0, height: 'auto' }}
                                exit={{ opacity: 0, y: -8, height: 0 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                style={{ overflow: 'hidden' }}
                            >
                                <GlassCard className="p-6 md:p-10">
                                    {!done && (
                                        <div className="flex justify-end -mt-2 mb-4">
                                            <button
                                                type="button"
                                                onClick={() => setExpanded(false)}
                                                className="text-xs uppercase tracking-[0.22em] text-[var(--tenant-muted)] hover:text-[var(--tenant-primary)] transition-colors"
                                            >
                                                {language === 'bn' ? '× বন্ধ করুন' : '× Close'}
                                            </button>
                                        </div>
                                    )}
                                    {done ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 mx-auto rounded-full bg-[var(--tenant-primary)]/15 flex items-center justify-center mb-4">
                                    <CheckCircle2 className="w-8 h-8 text-[var(--tenant-primary)]" />
                                </div>
                                <h3 className="font-display text-3xl tracking-wide text-[var(--tenant-ink)]">
                                    {language === 'bn' ? 'বুকিং পাঠানো হয়েছে' : 'Booking received'}
                                </h3>
                                <p className="mt-2 text-[var(--tenant-muted)]">
                                    {language === 'bn'
                                        ? 'আমরা শীঘ্রই কল করে নিশ্চিত করব।'
                                        : 'We\'ll call you shortly to confirm the slot.'}
                                </p>
                                <div className="mt-2 text-xs text-[var(--tenant-muted)]">Ref: {done.booking_id?.slice(0, 8)}</div>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="grid md:grid-cols-2 gap-6">
                                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <label className="block">
                                        <span className="text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)]">{language === 'bn' ? 'তারিখ' : 'Date'}</span>
                                        <div className="mt-2 flex items-center gap-2 px-3 h-11 rounded-xl border border-[var(--tenant-border)] bg-[var(--tenant-surface)]">
                                            <Calendar className="w-4 h-4 text-[var(--tenant-muted)]" />
                                            <input
                                                type="date"
                                                min={minDate}
                                                value={date}
                                                onChange={e => { setDate(e.target.value); setTime(''); }}
                                                className="flex-1 bg-transparent outline-none text-[var(--tenant-ink)]"
                                            />
                                        </div>
                                    </label>
                                    <label className="block sm:col-span-2">
                                        <span className="text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)]">{language === 'bn' ? 'সার্ভিস' : 'Service'}</span>
                                        <select
                                            value={serviceType}
                                            onChange={e => setServiceType(e.target.value)}
                                            className="mt-2 w-full px-3 h-11 rounded-xl border border-[var(--tenant-border)] bg-[var(--tenant-surface)] text-[var(--tenant-ink)] outline-none"
                                        >
                                            <option value="">{language === 'bn' ? 'সাধারণ পরিদর্শন' : 'General check-up'}</option>
                                            {(services || []).slice(0, 12).map(s => (
                                                <option key={s.id} value={s.name}>{s.name}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <div className="md:col-span-2">
                                    <span className="text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)]">{language === 'bn' ? 'খালি সময়' : 'Available slots'}</span>
                                    <div className="mt-3 flex flex-wrap gap-2 min-h-[44px]">
                                        {slotsLoading && <span className="text-sm text-[var(--tenant-muted)]"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />…</span>}
                                        {!slotsLoading && slots.length === 0 && (
                                            <span className="text-sm text-[var(--tenant-muted)]">{language === 'bn' ? 'এই দিন বন্ধ' : 'Closed that day'}</span>
                                        )}
                                        {slots.map(s => (
                                            <button
                                                type="button"
                                                key={s.time}
                                                disabled={!s.available}
                                                onClick={() => setTime(s.time)}
                                                className={[
                                                    'h-9 px-3 text-sm rounded-full border transition',
                                                    time === s.time
                                                        ? 'bg-[var(--tenant-primary)] text-[var(--tenant-surface)] border-transparent'
                                                        : s.available
                                                            ? 'border-[var(--tenant-border)] text-[var(--tenant-ink)] hover:border-[var(--tenant-primary)]'
                                                            : 'border-[var(--tenant-border)] text-[var(--tenant-muted)]/50 line-through cursor-not-allowed'
                                                ].join(' ')}
                                            >
                                                {s.time}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <Field label={language === 'bn' ? 'নাম' : 'Your name'} icon={<User className="w-4 h-4" />}>
                                    <input required value={form.customer_name} onChange={e => update('customer_name', e.target.value)} className="flex-1 bg-transparent outline-none text-[var(--tenant-ink)]" />
                                </Field>
                                <Field label={language === 'bn' ? 'ফোন' : 'Phone / WhatsApp'} icon={<Phone className="w-4 h-4" />}>
                                    <input required type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className="flex-1 bg-transparent outline-none text-[var(--tenant-ink)]" />
                                </Field>
                                <Field label={language === 'bn' ? 'গাড়ির ব্র্যান্ড' : 'Vehicle brand'}>
                                    <input value={form.bike_make} onChange={e => update('bike_make', e.target.value)} className="flex-1 bg-transparent outline-none text-[var(--tenant-ink)]" />
                                </Field>
                                <Field label={language === 'bn' ? 'মডেল' : 'Model'}>
                                    <input value={form.bike_model} onChange={e => update('bike_model', e.target.value)} className="flex-1 bg-transparent outline-none text-[var(--tenant-ink)]" />
                                </Field>
                                <label className="md:col-span-2 block">
                                    <span className="text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)]">{language === 'bn' ? 'নোট (ঐচ্ছিক)' : 'Notes (optional)'}</span>
                                    <textarea
                                        rows={3}
                                        value={form.notes}
                                        onChange={e => update('notes', e.target.value)}
                                        className="mt-2 w-full px-3 py-2 rounded-xl border border-[var(--tenant-border)] bg-[var(--tenant-surface)] text-[var(--tenant-ink)] outline-none resize-none"
                                    />
                                </label>

                                {error && (
                                    <div className="md:col-span-2 inline-flex items-center gap-2 text-sm text-red-400">
                                        <AlertCircle className="w-4 h-4" />{error}
                                    </div>
                                )}

                                <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
                                    <div className="text-sm text-[var(--tenant-muted)]">
                                        {language === 'bn' ? 'কোনো পেমেন্ট লাগছে না — শুধু স্লট রিজার্ভ।' : 'No payment now — just reserve your slot.'}
                                    </div>
                                    <MagneticButton
                                        as="button"
                                        type="submit"
                                        disabled={submitting}
                                        size="lg"
                                        icon={submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                                    >
                                        {language === 'bn' ? 'বুকিং পাঠান' : 'Submit booking'}
                                                    </MagneticButton>
                                                </div>
                                            </form>
                                        )}
                                    </GlassCard>
                                </motion.div>
                            )}
                        </AnimatePresence>
                </RevealOnScroll>
            </div>
        </section>
    );
}

function Field({ label, icon, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-[0.24em] text-[var(--tenant-muted)]">{label}</span>
            <div className="mt-2 flex items-center gap-2 px-3 h-11 rounded-xl border border-[var(--tenant-border)] bg-[var(--tenant-surface)]">
                {icon && <span className="text-[var(--tenant-muted)]">{icon}</span>}
                {children}
            </div>
        </label>
    );
}
